import { Injectable, inject } from '@angular/core'
import {
  HTTP_INTERCEPTORS,
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http'
import { Router } from '@angular/router'
import type { Observable } from 'rxjs'
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs'

import { AuthenticationService } from '@/app/core/services/auth.service'
import { AuthApiService } from '@/app/core/services/api/authentication/auth-api.service'
import { ResponseStatusCode } from '@/app/domain/enums/response-status-code.enum'

/** /api/auth/* and /api/health/* are anonymous — a 401 there is a login failure, not an expired session. */
const ANONYMOUS_PATH_PATTERN = /\/api\/(auth|health)(\/|$)/

/** Set on a request that's already been retried once with a freshly refreshed token — if it 401s
 *  again, the refresh token itself is bad server-side (or something is fundamentally wrong), so we
 *  sign out instead of refreshing forever. */
const RETRIED_AFTER_REFRESH = new HttpContextToken<boolean>(() => false)

/**
 * Access tokens live 15 minutes with zero server-side clock skew (see AddJwtBearer's
 * ClockSkew = TimeSpan.Zero), so any authenticated request made after that window gets a 401
 * straight from the JWT bearer middleware — not a ServiceResponse, since that's the one place in
 * the API that doesn't wrap everything as HTTP 200 (see ServiceResponse's own doc comment). This
 * interceptor transparently refreshes the access token via the stored refresh token (7-day
 * lifetime, single-use rotation — see RefreshTokenCommand) and retries the original request once.
 * Concurrent 401s from multiple in-flight requests share a single refresh call: the first 401
 * flips `isRefreshing` and calls /api/auth/refresh; every other 401 that arrives while that's in
 * flight just waits on `refreshTokenSubject` for the same result instead of calling refresh again.
 * Only when the refresh call itself fails (refresh token expired/revoked/reused) — or a retried
 * request 401s again — do we clear the session and bounce to sign-in with a returnUrl so the user
 * lands back where they were after logging in again.
 */
@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  private authService = inject(AuthenticationService)
  private authApi = inject(AuthApiService)
  private router = inject(Router)

  private isRefreshing = false
  private refreshTokenSubject = new BehaviorSubject<string | null>(null)

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (ANONYMOUS_PATH_PATTERN.test(request.url)) {
      return next.handle(request)
    }

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          if (request.context.get(RETRIED_AFTER_REFRESH)) {
            this.signOut()
            return throwError(() => error)
          }
          return this.handleUnauthorized(request, next)
        }
        return throwError(() => error)
      }),
    )
  }

  private handleUnauthorized(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const refreshToken = this.authService.refreshToken
    const employee = this.authService.employee

    if (!refreshToken || !employee) {
      this.signOut()
      return throwError(() => new HttpErrorResponse({ status: 401 }))
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true
      this.refreshTokenSubject.next(null)

      return this.authApi.refresh({ refreshToken }).pipe(
        switchMap((response) => {
          this.isRefreshing = false

          if (response.statusResult.code !== ResponseStatusCode.Success) {
            this.signOut()
            return throwError(() => new HttpErrorResponse({ status: 401 }))
          }

          const { authResult } = response.result
          this.authService.saveSession(authResult, employee, this.authService.menus)
          this.refreshTokenSubject.next(authResult.accessToken)

          return next.handle(this.addToken(request, authResult.accessToken))
        }),
        catchError((error: unknown) => {
          this.isRefreshing = false
          this.signOut()
          return throwError(() => error)
        }),
      )
    }

    return this.refreshTokenSubject.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addToken(request, token))),
    )
  }

  private addToken(request: HttpRequest<unknown>, accessToken: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${accessToken}` },
      context: new HttpContext().set(RETRIED_AFTER_REFRESH, true),
    })
  }

  private signOut(): void {
    this.authService.clearSession()
    this.router.navigate(['/auth/sign-in'], { queryParams: { returnUrl: this.router.url } })
  }
}

export const AuthErrorInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthErrorInterceptor,
  multi: true,
}
